import React from 'react';

import { TrackableFile, CreateTrackableFile } from 'types/trackables';

import { FileUploadOptions, handleFileUpload } from 'services/file_upload';
import { addErrorNotification } from 'services/notification';

import { useCreateTrackableFile } from './useCreateTrackableFile';
import { useUpdateTrackableFileAvailability } from './useUpdateTrackableFileAvailability';

export type TrackableUploadFile = Pick<CreateTrackableFile, 'name' | 'size'> & {
  trackable_list_id: string;
  trackable_item_id: string;
};


export function useUploadTrackableFile() {

  const { mutateAsync: createTrackableFile } = useCreateTrackableFile({
    onSuccess: () => {
      // Don't invalidate queries here - will do it once at the end
    },
    onError(error) {
      addErrorNotification({ title: 'Unable to create trackable file.', error });
    },
  });

  const { mutateAsync: updateAvailability } = useUpdateTrackableFileAvailability({
    onSuccess: () => {
      // Don't invalidate queries here - will do it once at the end
    },
    onError(error) {
      addErrorNotification({ title: 'Unable to update file availability.', error });
    },
  });

  const [uploadProgress, setUploadProgress] = React.useState({ current: 0, total: 0, currentFile: '' });
  const [isUploading, setIsUploading] = React.useState(false);

  const uploadTrackableFile = React.useCallback(
    async (file: File, params: TrackableUploadFile, options?: FileUploadOptions) => {
      // Step 1: Create the trackable file record
      const apiFile: TrackableFile = await createTrackableFile({
        trackable_list_id: params.trackable_list_id,
        trackable_item_id: params.trackable_item_id,
        data: {
          name: params.name || file.name,
          size: file.size,
        },
      });

      if (!apiFile.data_url) {
        throw new Error('No upload URL received from API');
      }

      // Step 2: Upload file to S3
      await handleFileUpload(apiFile.data_url, file, options);

      // Step 3: Update file availability
      await updateAvailability({
        trackable_list_id: params.trackable_list_id,
        trackable_item_id: params.trackable_item_id,
        trackable_file_id: apiFile.id,
      });

      return apiFile;
    },
    [createTrackableFile, updateAvailability]
  );

  const uploadMultipleTrackableFiles = React.useCallback(
    async (files: File[], params: Omit<TrackableUploadFile, 'name' | 'size'>, options?: FileUploadOptions) => {
      try {
        setIsUploading(true);
        const results: TrackableFile[] = [];

        // Upload files sequentially
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          setUploadProgress({
            current: i + 1,
            total: files.length,
            currentFile: file.name,
          });

          try {
            const result = await uploadTrackableFile(file, {
              ...params,
              name: file.name,
              size: file.size,
            }, options);
            results.push(result);
          } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);
            // Continue with next file even if one fails
          }
        }

        // Clean up progress after ALL uploads complete
        // NOTE: Do NOT invalidate queries here - let the caller do it after dialog closes
        setUploadProgress({ current: 0, total: 0, currentFile: '' });

        return results;
      } finally {
        setIsUploading(false);
      }
    },
    [uploadTrackableFile]
  );

  return {
    uploadTrackableFile,
    uploadMultipleTrackableFiles,
    uploadProgress,
    isLoading: isUploading,
  };
}